// @ts-ignore
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Badge,
  Button,
  Form,
  InputGroup,
  OverlayTrigger,
  Tooltip,
  Alert,
  Modal
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers,
  faSearch,
  faFilter,
  faDownload,
  faEdit,
  faHistory,
  faCheckCircle,
  faExclamationTriangle,
  faClock,
  faCalendarAlt,
  faChartLine,
  faDollarSign,
  faFileContract,
  faArrowUp,
  faArrowDown
} from '@fortawesome/free-solid-svg-icons';

interface AgentPlan {
  agentId: string;
  agentName: string;
  licenseType: 'BR' | 'SA';
  currentPlan: string;
  planVersion: number;
  effectiveDate: string;
  nextReviewDate?: string;
  agentSplit: number;
  transactionFee: number;
  monthlyFee: number;
  ytdGci: number;
  totalTransactions: number;
  currentTier?: string;
  suggestedPlan?: string;
  status: 'active' | 'scheduled' | 'needs_review';
}

interface PlanHistory {
  id: string;
  templateName: string;
  effectiveStartDate: string;
  effectiveEndDate?: string;
  status: string;
  agentSplit: number;
  monthlyFee: number;
}

interface PlanChange {
  id: string;
  changeType: 'created' | 'updated' | 'terminated' | 'expired';
  changeDate: string;
  changedBy: string;
  changedByName?: string;
  oldPlan?: string;
  newPlan?: string;
  reason?: string;
  notes?: string;
}

const AgentPlanView: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [agents, setAgents] = useState<AgentPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterPlan, setFilterPlan] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Commission plan assignment modal state
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<AgentPlan | null>(null);
  const [commissionTemplates, setCommissionTemplates] = useState<any[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().split('T')[0]);
  const [assignmentNotes, setAssignmentNotes] = useState('');
  const [monthlyFee, setMonthlyFee] = useState(0);
  const [planHistory, setPlanHistory] = useState<PlanHistory[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // History modal state
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedAgentHistory, setSelectedAgentHistory] = useState<AgentPlan | null>(null);
  const [planChanges, setPlanChanges] = useState<PlanChange[]>([]);
  const [fullPlanHistory, setFullPlanHistory] = useState<PlanHistory[]>([]);

  // Get brokerage ID from localStorage
  const brokerageId = localStorage.getItem('brokerageId');

  useEffect(() => {
    // Check URL parameters for filter
    const filterParam = searchParams.get('filter');
    if (filterParam) {
      setFilterPlan(filterParam.toLowerCase());
    }
    
    fetchAgentPlans();
    if (brokerageId) {
      fetchCommissionTemplates();
    }
  }, [brokerageId, searchParams]);

  const fetchAgentPlans = async () => {
    if (!brokerageId) {
      setError('No brokerage ID found');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`/api/commission-plans/agents/${brokerageId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch agent plans');
      }

      const data = await response.json();
      if (data.success) {
        // Transform the data to match our interface
        const transformedAgents = data.agentPlans.map((agent: any) => ({
          agentId: agent.agent_id,
          agentName: `${agent.first_name} ${agent.last_name}`,
          licenseType: agent.license_type === 'Broker' ? 'BR' : 'SA',
          currentPlan: agent.plan_name || '80/20 Split',
          planVersion: agent.plan_version || 1,
          effectiveDate: agent.plan_effective_date || agent.effective_date,
          nextReviewDate: agent.next_review_date,
          agentSplit: agent.agent_split || 80,
          transactionFee: agent.transaction_fee || 295,
          monthlyFee: agent.monthly_fee || 0,
          ytdGci: agent.ytd_gci || 0,
          totalTransactions: agent.total_transactions || 0,
          currentTier: agent.current_tier,
          suggestedPlan: agent.suggested_plan,
          status: determineStatus(agent)
        }));
        setAgents(transformedAgents);
      } else {
        throw new Error(data.message || 'Failed to load agent plans');
      }
    } catch (err: any) {
      console.error('Error fetching agent plans:', err);
      setError(err.message || 'Failed to load agent commission plans');
    } finally {
      setLoading(false);
    }
  };

  const fetchCommissionTemplates = async () => {
    try {
      const response = await fetch(`/api/commission-plans/templates/${brokerageId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setCommissionTemplates(data.templates);
        }
      }
    } catch (err) {
      console.error('Error fetching commission templates:', err);
    }
  };

  const handleAssignPlan = async () => {
    if (!selectedAgent || !selectedTemplateId) return;

    try {
      const response = await fetch('/api/commission-plans/assign', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          agentId: selectedAgent.agentId,
          templateId: selectedTemplateId,
          effectiveStartDate: effectiveDate,
          notes: assignmentNotes,
          monthlyFee: monthlyFee
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Refresh agent plans
          await fetchAgentPlans();
          handleCloseModal();
          // Show success message if needed
        } else {
          throw new Error(data.message || 'Failed to assign plan');
        }
      } else {
        throw new Error('Failed to assign commission plan');
      }
    } catch (err: any) {
      console.error('Error assigning commission plan:', err);
      setError(err.message || 'Failed to assign commission plan');
    }
  };

  const fetchAgentPlanHistory = async (agentId: string) => {
    setLoadingHistory(true);
    try {
      const response = await fetch(`/api/commission-plans/agent/${agentId}/history`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setPlanHistory(data.history);
        }
      }
    } catch (err) {
      console.error('Error fetching plan history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleEditPlan = async (agent: AgentPlan) => {
    setSelectedAgent(agent);
    
    // Fetch plan history for this agent
    await fetchAgentPlanHistory(agent.agentId);
    
    // Find the template that matches the agent's current plan
    const currentTemplate = commissionTemplates.find(template => 
      template.name === agent.currentPlan
    );
    
    // Pre-select the current template if found
    setSelectedTemplateId(currentTemplate ? currentTemplate.id : '');
    setEffectiveDate(new Date().toISOString().split('T')[0]);
    setAssignmentNotes('');
    setMonthlyFee(agent.monthlyFee || 0);
    setShowAssignModal(true);
  };

  const handleCloseModal = () => {
    setShowAssignModal(false);
    setSelectedAgent(null);
    setSelectedTemplateId('');
    setAssignmentNotes('');
    setMonthlyFee(0);
    setPlanHistory([]);
  };

  const handleViewHistory = async (agent: AgentPlan) => {
    setSelectedAgentHistory(agent);
    setLoadingHistory(true);
    
    try {
      // Fetch both plan history and changes
      const [historyResponse, changesResponse] = await Promise.all([
        fetch(`/api/commission-plans/agent/${agent.agentId}/history`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch(`/api/commission-plans/agent/${agent.agentId}/changes`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          }
        })
      ]);

      if (historyResponse.ok) {
        const data = await historyResponse.json();
        if (data.success) {
          setFullPlanHistory(data.history);
        }
      }

      if (changesResponse.ok) {
        const data = await changesResponse.json();
        if (data.success) {
          setPlanChanges(data.changes);
        }
      }
    } catch (err) {
      console.error('Error fetching history:', err);
    } finally {
      setLoadingHistory(false);
      setShowHistoryModal(true);
    }
  };

  const determineStatus = (agent: any): 'active' | 'scheduled' | 'needs_review' => {
    // If agent is terminated
    if (agent.termination_date && new Date(agent.termination_date) <= new Date()) {
      return 'needs_review';
    }
    
    // If agent has a scheduled future plan
    if (agent.next_effective_date && new Date(agent.next_effective_date) > new Date()) {
      return 'scheduled';
    }
    
    // If review date has passed
    if (agent.next_review_date && new Date(agent.next_review_date) < new Date()) {
      return 'needs_review';
    }
    
    return 'active';
  };

  const getPlanBadgeColor = (plan: string) => {
    if (plan.includes('High Producer')) return 'primary';
    if (plan.includes('Elite')) return 'success';
    return 'secondary';
  };

  const getStatusBadge = (agent: AgentPlan) => {
    if (agent.status === 'needs_review') {
      return (
        <Badge bg="warning">
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
          Needs Review
        </Badge>
      );
    }
    if (agent.status === 'scheduled') {
      return (
        <Badge bg="info">
          <FontAwesomeIcon icon={faClock} className="me-1" />
          Scheduled
        </Badge>
      );
    }
    return (
      <Badge bg="success">
        <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
        Active
      </Badge>
    );
  };

  const getPerformanceIndicator = (agent: AgentPlan) => {
    if (agent.suggestedPlan) {
      return (
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip>
              Performance qualifies for {agent.suggestedPlan}
            </Tooltip>
          }
        >
          <Badge bg="info" className="ms-2">
            <FontAwesomeIcon icon={faArrowUp} />
          </Badge>
        </OverlayTrigger>
      );
    }
    return null;
  };

  // Filter agents based on search and filters
  const filteredAgents = agents.filter(agent => {
    const matchesSearch = agent.agentName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPlan = filterPlan === 'all' || agent.currentPlan.toLowerCase().includes(filterPlan.toLowerCase());
    const matchesStatus = filterStatus === 'all' || agent.status === filterStatus;
    return matchesSearch && matchesPlan && matchesStatus;
  });

  // Calculate summary statistics based on filtered agents
  const avgAgentSplit = filteredAgents.length > 0 
    ? Math.round(filteredAgents.reduce((sum, a) => sum + a.agentSplit, 0) / filteredAgents.length * 10) / 10
    : 0;

  const avgTransactionFee = filteredAgents.length > 0
    ? Math.round(filteredAgents.reduce((sum, a) => sum + a.transactionFee, 0) / filteredAgents.length)
    : 0;

  const monthlyRevenue = filteredAgents.reduce((sum, a) => sum + a.monthlyFee, 0);
  const needsReviewCount = filteredAgents.filter(a => a.status === 'needs_review').length;

  if (loading) {
    return (
      <Container fluid className="p-4">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-muted">Loading agent commission plans...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="p-4">
        <Alert variant="danger">
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="p-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h4 className="mb-1">
                <FontAwesomeIcon icon={faUsers} className="me-2 text-primary" />
                Agent Commission Plans
              </h4>
              <p className="text-muted mb-0">
                View and manage all agent commission assignments
              </p>
            </div>
            <div className="d-flex gap-2">
              <Button variant="outline-primary">
                <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                Schedule Yearly Review
              </Button>
              <Button variant="primary">
                <FontAwesomeIcon icon={faFileContract} className="me-2" />
                Bulk Assign Plans
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {/* Alerts */}
      {needsReviewCount > 0 && (
        <Row className="mb-3">
          <Col>
            <Alert variant="warning" className="d-flex align-items-center">
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
              <div>
                <strong>{needsReviewCount} agent{needsReviewCount !== 1 ? 's' : ''} need commission plan review</strong> - 
                Their annual review date has passed or performance metrics suggest a plan change.
                <Button 
                  variant="link" 
                  size="sm" 
                  className="ms-2 p-0"
                  onClick={() => setFilterStatus('needs_review')}
                >
                  View all →
                </Button>
              </div>
            </Alert>
          </Col>
        </Row>
      )}

      {/* Filters */}
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body>
          <Row className="g-3">
            <Col md={4}>
              <InputGroup>
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faSearch} />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search agents..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Select
                value={filterPlan}
                onChange={(e) => setFilterPlan(e.target.value)}
              >
                <option value="all">All Plans</option>
                {commissionTemplates.map(template => (
                  <option key={template.id} value={template.name.toLowerCase()}>
                    {template.name}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="needs_review">Needs Review</option>
                <option value="scheduled">Scheduled</option>
              </Form.Select>
            </Col>
            <Col md={2} className="text-end">
              <Button variant="outline-secondary">
                <FontAwesomeIcon icon={faDownload} className="me-2" />
                Export
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Agent Plans Table */}
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          <Table responsive hover className="mb-0">
            <thead className="bg-light">
              <tr>
                <th className="border-0 px-4 py-3">Agent</th>
                <th className="border-0 py-3">Current Plan</th>
                <th className="border-0 py-3">Commission</th>
                <th className="border-0 py-3">Fees</th>
                <th className="border-0 py-3">YTD Performance</th>
                <th className="border-0 py-3">Status</th>
                <th className="border-0 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAgents.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-4 text-muted">
                    {agents.length === 0 
                      ? 'No agents found in your brokerage'
                      : 'No agents match your search criteria'}
                  </td>
                </tr>
              ) : (
                filteredAgents.map(agent => (
                  <tr key={agent.agentId}>
                  <td className="px-4 py-3">
                    <div className="d-flex align-items-center">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center me-3"
                        style={{
                          width: '40px',
                          height: '40px',
                          background: agent.licenseType === 'BR' ? '#10b981' : '#00d4ff',
                          color: 'white',
                          fontSize: '0.8rem',
                          fontWeight: 'bold'
                        }}
                      >
                        {agent.licenseType}
                      </div>
                      <div>
                        <div className="fw-semibold">{agent.agentName}</div>
                        <small className="text-muted">
                          v{agent.planVersion} • Since {new Date(agent.effectiveDate).toLocaleDateString()}
                        </small>
                      </div>
                    </div>
                  </td>
                  <td className="py-3">
                    <Badge bg={getPlanBadgeColor(agent.currentPlan)}>
                      {agent.currentPlan}
                    </Badge>
                    {getPerformanceIndicator(agent)}
                    {agent.currentTier && (
                      <div className="small text-muted mt-1">
                        Tier: {agent.currentTier}
                      </div>
                    )}
                  </td>
                  <td className="py-3">
                    <div className="fw-semibold">{agent.agentSplit}%</div>
                    <small className="text-muted">Agent split</small>
                  </td>
                  <td className="py-3">
                    <div className="small">
                      <div>${agent.transactionFee} transaction</div>
                      <div className="text-muted">${agent.monthlyFee}/month</div>
                    </div>
                  </td>
                  <td className="py-3">
                    <div className="d-flex align-items-center gap-3">
                      <div>
                        <div className="small text-muted">GCI</div>
                        <div className="fw-semibold">${agent.ytdGci.toLocaleString()}</div>
                      </div>
                      <div>
                        <div className="small text-muted">Deals</div>
                        <div className="fw-semibold">{agent.totalTransactions}</div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3">
                    {getStatusBadge(agent)}
                    {agent.nextReviewDate && (
                      <div className="small text-muted mt-1">
                        Review: {new Date(agent.nextReviewDate).toLocaleDateString()}
                      </div>
                    )}
                  </td>
                  <td className="py-3 text-center">
                    <div className="d-flex justify-content-center gap-1">
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Edit Plan</Tooltip>}
                      >
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={() => handleEditPlan(agent)}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </Button>
                      </OverlayTrigger>
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>View History</Tooltip>}
                      >
                        <Button 
                          variant="outline-secondary" 
                          size="sm"
                          onClick={() => handleViewHistory(agent)}
                        >
                          <FontAwesomeIcon icon={faHistory} />
                        </Button>
                      </OverlayTrigger>
                    </div>
                  </td>
                </tr>
              )))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Summary Stats */}
      <Row className="mt-4">
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <h6 className="text-muted mb-2">Avg Agent Split</h6>
              <h3 className="mb-0 text-primary">{avgAgentSplit}%</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <h6 className="text-muted mb-2">Avg Transaction Fee</h6>
              <h3 className="mb-0 text-success">${avgTransactionFee}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <h6 className="text-muted mb-2">Monthly Revenue</h6>
              <h3 className="mb-0 text-info">${monthlyRevenue.toLocaleString()}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <h6 className="text-muted mb-2">Plans Needing Review</h6>
              <h3 className="mb-0 text-warning">{needsReviewCount}</h3>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Commission Plan Assignment Modal */}
      <Modal show={showAssignModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faFileContract} className="me-2" />
            Assign Commission Plan
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedAgent && (
            <>
              <div className="mb-4">
                <h6 className="text-muted mb-2">Agent Information</h6>
                <Card className="border-0 bg-light">
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <p className="mb-1"><strong>Name:</strong> {selectedAgent.agentName}</p>
                        <p className="mb-0"><strong>License Type:</strong> {selectedAgent.licenseType}</p>
                      </Col>
                      <Col md={6}>
                        <p className="mb-1"><strong>Current Plan:</strong> {selectedAgent.currentPlan}</p>
                        <p className="mb-0"><strong>Current Split:</strong> {selectedAgent.agentSplit}%</p>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </div>

              {/* Plan History Timeline */}
              {planHistory.length > 0 && (
                <div className="mb-4">
                  <h6 className="text-muted mb-3">Commission Plan Timeline</h6>
                  <div className="timeline-container" style={{ position: 'relative', paddingLeft: '30px' }}>
                    {planHistory.map((plan, index) => {
                      const isActive = plan.status === 'active' && !plan.effectiveEndDate;
                      const isFuture = new Date(plan.effectiveStartDate) > new Date();
                      
                      return (
                        <div key={plan.id} className="mb-3" style={{ position: 'relative' }}>
                          {/* Timeline dot */}
                          <div
                            style={{
                              position: 'absolute',
                              left: '-30px',
                              top: '5px',
                              width: '12px',
                              height: '12px',
                              borderRadius: '50%',
                              backgroundColor: isActive ? '#28a745' : isFuture ? '#17a2b8' : '#6c757d',
                              border: '2px solid #fff',
                              boxShadow: '0 0 0 1px #dee2e6'
                            }}
                          />
                          
                          {/* Timeline line */}
                          {index < planHistory.length - 1 && (
                            <div
                              style={{
                                position: 'absolute',
                                left: '-25px',
                                top: '17px',
                                width: '2px',
                                height: '100%',
                                backgroundColor: '#dee2e6'
                              }}
                            />
                          )}
                          
                          {/* Plan details */}
                          <Card 
                            className={`border-0 ${isActive ? 'border-start border-success border-3' : ''}`}
                            style={{ backgroundColor: isActive ? '#f8f9fa' : '#fff' }}
                          >
                            <Card.Body className="py-2 px-3">
                              <div className="d-flex justify-content-between align-items-start">
                                <div>
                                  <strong>{plan.templateName}</strong>
                                  <div className="small text-muted">
                                    {new Date(plan.effectiveStartDate).toLocaleDateString()} - {
                                      plan.effectiveEndDate 
                                        ? new Date(plan.effectiveEndDate).toLocaleDateString()
                                        : 'Ongoing'
                                    }
                                  </div>
                                  <div className="small">
                                    <Badge bg="light" text="dark" className="me-2">
                                      {plan.agentSplit}% split
                                    </Badge>
                                    <Badge bg="light" text="dark">
                                      ${plan.monthlyFee}/month
                                    </Badge>
                                  </div>
                                </div>
                                <div>
                                  {isActive && (
                                    <Badge bg="success" className="ms-2">Current</Badge>
                                  )}
                                  {isFuture && (
                                    <Badge bg="info" className="ms-2">Future</Badge>
                                  )}
                                </div>
                              </div>
                            </Card.Body>
                          </Card>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Select Commission Plan Template</Form.Label>
                  <Form.Select
                    value={selectedTemplateId}
                    onChange={(e) => setSelectedTemplateId(e.target.value)}
                    required
                  >
                    <option value="">Choose a commission plan...</option>
                    {commissionTemplates.map(template => (
                      <option key={template.id} value={template.id}>
                        {template.name} - {template.baseAgentSplit}% Agent / {template.baseBrokerSplit}% Broker
                        {template.transactionFee > 0 && ` + $${template.transactionFee} fee`}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Select from your brokerage's commission plan templates
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Effective Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={effectiveDate}
                    onChange={(e) => setEffectiveDate(e.target.value)}
                    required
                  />
                  <Form.Text className="text-muted">
                    You can backdate or future-date this assignment. Existing plans will be adjusted automatically.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Monthly Agent Fee ($)</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="1"
                    value={monthlyFee}
                    onChange={(e) => setMonthlyFee(Number(e.target.value))}
                    placeholder="0"
                  />
                  <Form.Text className="text-muted">
                    Total monthly fee charged to agent. Brokerage keeps ${(monthlyFee - 70) > 0 ? (monthlyFee - 70).toFixed(2) : '0'} after $70 KairosIQ platform fee.
                    {monthlyFee > 0 && monthlyFee < 70 && (
                      <span className="text-warning d-block mt-1">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                        Warning: Fee is less than $70 platform cost
                      </span>
                    )}
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Notes (Optional)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={assignmentNotes}
                    onChange={(e) => setAssignmentNotes(e.target.value)}
                    placeholder="Add any notes about this plan assignment..."
                  />
                </Form.Group>

                {(() => {
                  const selectedDate = new Date(effectiveDate);
                  const existingPlans = planHistory.filter(plan => 
                    !plan.effectiveEndDate || new Date(plan.effectiveEndDate) >= selectedDate
                  );
                  
                  if (existingPlans.length > 0) {
                    const affectedPlan = existingPlans[0];
                    const willDelete = new Date(affectedPlan.effectiveStartDate) >= selectedDate;
                    
                    return (
                      <Alert variant={willDelete ? "warning" : "info"}>
                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                        {willDelete ? (
                          <>
                            <strong>Warning:</strong> The scheduled "{affectedPlan.templateName}" plan starting on{' '}
                            {new Date(affectedPlan.effectiveStartDate).toLocaleDateString()} will be replaced by this new assignment.
                          </>
                        ) : (
                          <>
                            <strong>Plan Transition:</strong> The current "{affectedPlan.templateName}" plan will automatically end on{' '}
                            {new Date(new Date(effectiveDate).getTime() - 86400000).toLocaleDateString()} when this new plan takes effect.
                          </>
                        )}
                      </Alert>
                    );
                  }
                  return null;
                })()}
              </Form>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleAssignPlan}
            disabled={!selectedTemplateId}
          >
            <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
            Assign Plan
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Commission Plan History Modal */}
      <Modal show={showHistoryModal} onHide={() => setShowHistoryModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faHistory} className="me-2" />
            Commission Plan History - {selectedAgentHistory?.agentName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingHistory ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <>
              {/* Plan Timeline */}
              <div className="mb-5">
                <h6 className="mb-3">Commission Plan Timeline</h6>
                {fullPlanHistory.length === 0 ? (
                  <Alert variant="info">No commission plan history found for this agent.</Alert>
                ) : (
                  <div className="timeline-horizontal">
                    <Row className="g-3">
                      {fullPlanHistory.map((plan, index) => {
                        const isActive = plan.status === 'active' && !plan.effectiveEndDate;
                        const isFuture = new Date(plan.effectiveStartDate) > new Date();
                        
                        return (
                          <Col md={4} key={plan.id}>
                            <Card className={`h-100 ${isActive ? 'border-success' : isFuture ? 'border-info' : 'border-secondary'}`}>
                              <Card.Body>
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                  <h6 className="mb-0">{plan.templateName}</h6>
                                  {isActive && <Badge bg="success">Active</Badge>}
                                  {isFuture && <Badge bg="info">Future</Badge>}
                                </div>
                                <div className="small text-muted mb-2">
                                  {new Date(plan.effectiveStartDate).toLocaleDateString()} - {
                                    plan.effectiveEndDate 
                                      ? new Date(plan.effectiveEndDate).toLocaleDateString()
                                      : 'Ongoing'
                                  }
                                </div>
                                <div>
                                  <Badge bg="light" text="dark" className="me-2">
                                    {plan.agentSplit}% split
                                  </Badge>
                                  <Badge bg="light" text="dark">
                                    ${plan.monthlyFee}/month
                                  </Badge>
                                </div>
                              </Card.Body>
                            </Card>
                          </Col>
                        );
                      })}
                    </Row>
                  </div>
                )}
              </div>

              {/* Change History */}
              <div>
                <h6 className="mb-3">Change History</h6>
                {planChanges.length === 0 ? (
                  <Alert variant="info">No change history recorded.</Alert>
                ) : (
                  <Table responsive hover className="small">
                    <thead className="bg-light">
                      <tr>
                        <th>Date</th>
                        <th>Action</th>
                        <th>Changed By</th>
                        <th>From Plan</th>
                        <th>To Plan</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {planChanges.map((change) => (
                        <tr key={change.id}>
                          <td>{new Date(change.changeDate).toLocaleString()}</td>
                          <td>
                            <Badge bg={
                              change.changeType === 'created' ? 'success' :
                              change.changeType === 'updated' ? 'primary' :
                              change.changeType === 'terminated' ? 'danger' : 'secondary'
                            }>
                              {change.changeType}
                            </Badge>
                          </td>
                          <td>{change.changedByName || 'System'}</td>
                          <td>{change.oldPlan || '-'}</td>
                          <td>{change.newPlan || '-'}</td>
                          <td className="text-truncate" style={{ maxWidth: '200px' }}>
                            {change.reason || change.notes || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowHistoryModal(false);
            setSelectedAgentHistory(null);
            setFullPlanHistory([]);
            setPlanChanges([]);
          }}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AgentPlanView;